// ft3d-hud/draggable.ts — drag a window by its header, clamped to the frame, position remembered.
//
// One implementation for every FT3D window family: the HUD's own (dice, forms) and my_campaign.php's
// campaign kit (settings, workspace, actor). The route says which element is the frame; everything
// else — the freeze-then-pin step, clamping, persistence — is the same everywhere.
//
// Position is per-viewer and per-window, kept in localStorage. It is a convenience, never state the
// page depends on: every window still has a CSS-declared corner it uses until it is first dragged,
// and a stored position that no longer fits the viewport is discarded rather than honoured.

const POSITION_KEY = 'ft3d.hud.windowPositions';
const POSITION_STYLE_PROPERTIES = [
    'position',
    'right',
    'bottom',
    'transform',
    'width',
    'height',
    'left',
    'top',
] as const;

interface StoredPosition {
    left: number;
    top: number;
}

interface InlineStyleValue {
    value: string;
    priority: string;
}

interface TransientPositionSnapshot {
    styles: Record<(typeof POSITION_STYLE_PROPERTIES)[number], InlineStyleValue>;
    positioned: boolean;
}

const transientPositionSnapshots = new WeakMap<HTMLElement, TransientPositionSnapshot>();

function readPositions(): Record<string, StoredPosition> {
    try {
        const raw = window.localStorage.getItem(POSITION_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw) as unknown;
        return (parsed && typeof parsed === 'object') ? parsed as Record<string, StoredPosition> : {};
    } catch {
        return {};
    }
}

function writePosition(name: string, position: StoredPosition | null): void {
    try {
        const all = readPositions();
        if (position) all[name] = position;
        else delete all[name];
        window.localStorage.setItem(POSITION_KEY, JSON.stringify(all));
    } catch { /* viewer-local nicety only */ }
}

function frameRectFor(win: HTMLElement, frame: HTMLElement | null): DOMRect {
    if (frame) return frame.getBoundingClientRect();
    // No named frame — the viewport is the frame. Built by hand because DOMRect has no such ctor
    // for a plain box in every supported engine.
    return new DOMRect(0, 0, window.innerWidth, window.innerHeight);
}

function clamp(value: number, min: number, max: number): number {
    // max can fall BELOW min when the window is larger than the frame; pinning to min keeps the
    // header — the only thing you can drag by — on screen.
    return Math.max(min, Math.min(Math.max(min, max), value));
}

/**
 * A `hidden` (display:none) element measures 0×0, and applyPosition() FREEZES what it measures.
 * Pinning those zeros writes `width: 0; height: 0` inline and the window is then a ghost forever:
 * it un-hides, it toggles, it takes the exclusive slot from Campaign time — and nothing appears.
 * That was the owner's 2026-08-21 bug (settings/workspace/actor start `hidden` in the markup, so a
 * restore at activation time hit all three; time/gm start visible and were fine).
 *
 * ⚠ "Non-zero" is not the same as "laid out". A window caught mid-reveal can measure a couple of
 * border pixels, and freezing THAT is the same bug in a smaller font. A real window is never this
 * small, so the floor doubles as a "has it actually been laid out yet" test.
 */
const MIN_MEASURABLE_PX = 24;

function isMeasurable(win: HTMLElement): boolean {
    const rect = win.getBoundingClientRect();
    return rect.width >= MIN_MEASURABLE_PX && rect.height >= MIN_MEASURABLE_PX;
}

/**
 * One armed restore per element, ever. Without this, anything that re-runs the mount (a re-render,
 * a second activation, a route that mounts the HUD twice) stacks another observer on the same
 * window, and they all fire.
 */
const armedRestores = new WeakSet<HTMLElement>();

/**
 * Pin a window at an absolute position inside the frame's coordinate space. Freezes the measured box
 * first: a window anchored by `right`/`bottom` would otherwise jump the moment `left`/`top` win.
 */
function applyPosition(win: HTMLElement, frame: HTMLElement | null, left: number, top: number): void {
    const rect = win.getBoundingClientRect();
    // ⛔ NEVER freeze a box that measures nothing — see isMeasurable(). Callers should not reach
    // here with a hidden window, but this is the floor that makes the ghost impossible rather than
    // merely unlikely: refusing to write `width: 0` costs one skipped restore, writing it costs the
    // window permanently.
    if (rect.width <= 0 || rect.height <= 0) return;
    const frameRect = frameRectFor(win, frame);
    const maxLeft = frameRect.width - rect.width;
    const maxTop = frameRect.height - rect.height;
    win.style.position = 'fixed';
    win.style.right = 'auto';
    win.style.bottom = 'auto';
    // A centred window carries translateX(-50%); leaving it would offset every dragged position by
    // half its width.
    win.style.transform = 'none';
    win.style.width = `${Math.round(rect.width)}px`;
    // ⛔ Freeze the HEIGHT too. The campaign windows are anchored top+bottom, so their height comes
    // from that pair; setting `bottom: auto` alone collapsed them to content height, which in turn
    // made `.ft3d-campaign-window__content { height: calc(100% - bar) }` meaningless and killed the
    // inner scroll. Pinning the measured height keeps the box exactly as the viewer saw it.
    if (!win.style.height) win.style.height = `${Math.round(rect.height)}px`;
    win.style.left = `${Math.round(frameRect.left + clamp(left, 0, maxLeft))}px`;
    win.style.top = `${Math.round(frameRect.top + clamp(top, 0, maxTop))}px`;
    win.classList.add('is-positioned');
}

/**
 * Temporarily pin a window through the exact same measurement and clamp path used by dragging.
 * The first call snapshots only the inline properties applyPosition() owns; later calls may
 * reconcile the nudge without replacing that baseline.
 */
export function applyTransientFt3dWindowPosition(
    win: HTMLElement,
    frame: HTMLElement | null,
    left: number,
    top: number,
): void {
    if (!transientPositionSnapshots.has(win)) {
        const styles = {} as TransientPositionSnapshot['styles'];
        POSITION_STYLE_PROPERTIES.forEach((property) => {
            styles[property] = {
                value: win.style.getPropertyValue(property),
                priority: win.style.getPropertyPriority(property),
            };
        });
        transientPositionSnapshots.set(win, {
            styles,
            positioned: win.classList.contains('is-positioned'),
        });
    }
    applyPosition(win, frame, left, top);
}

/** Restore the exact pre-nudge inline geometry without touching a remembered drag position. */
export function clearTransientFt3dWindowPosition(win: HTMLElement): boolean {
    const snapshot = transientPositionSnapshots.get(win);
    if (!snapshot) return false;
    POSITION_STYLE_PROPERTIES.forEach((property) => {
        const previous = snapshot.styles[property];
        if (previous.value) win.style.setProperty(property, previous.value, previous.priority);
        else win.style.removeProperty(property);
    });
    win.classList.toggle('is-positioned', snapshot.positioned);
    transientPositionSnapshots.delete(win);
    return true;
}

/**
 * Re-apply a remembered position, discarding one that no longer fits this viewport.
 *
 * ⚠ A restore is only meaningful once the window is ON SCREEN. Windows that start `hidden` are
 * wired up long before they are first opened, so this defers to the moment the window is actually
 * shown instead of measuring a display:none box — measuring it there is what pinned `width: 0`
 * and produced a window that toggled but never appeared. Deferring also keeps the fit check
 * honest: it compares the stored corner against the window's REAL size, not against zero.
 */
export function restoreFt3dWindowPosition(
    name: string,
    win: HTMLElement,
    frame: HTMLElement | null,
): void {
    const stored = readPositions()[name];
    if (!stored) return;

    const applyStored = (): void => {
        const frameRect = frameRectFor(win, frame);
        const rect = win.getBoundingClientRect();
        // A window remembered on a much wider screen would otherwise open mostly off the edge.
        if (stored.left > frameRect.width - Math.min(rect.width, 80) || stored.top > frameRect.height - 24) {
            writePosition(name, null);
            return;
        }
        applyPosition(win, frame, stored.left, stored.top);
    };

    if (isMeasurable(win)) {
        applyStored();
        return;
    }
    if (armedRestores.has(win)) return;
    armedRestores.add(win);

    // Not on screen yet. Wait for it to HAVE A BOX, and let a frame pass so a reveal that animates
    // is measured settled rather than mid-transition.
    const fire = (stop: () => void): void => {
        if (!isMeasurable(win)) return;
        stop();
        window.requestAnimationFrame(() => {
            if (isMeasurable(win)) applyStored();
            armedRestores.delete(win);
        });
    };

    // ⚠ ResizeObserver, not MutationObserver: the question is "does this window have a box yet",
    // and only a size observer answers it for every route to getting one. An attribute watcher on
    // the window itself misses a reveal driven by an ANCESTOR's hidden/class/style, and misses a
    // node that is detached while hidden and reinserted later — neither of those mutates anything
    // on the window. MutationObserver stays as the fallback for engines without ResizeObserver.
    if (typeof ResizeObserver === 'function') {
        const resizeObserver = new ResizeObserver(() => fire(() => resizeObserver.disconnect()));
        resizeObserver.observe(win);
        return;
    }
    const observer = new MutationObserver(() => fire(() => observer.disconnect()));
    observer.observe(win, { attributes: true, attributeFilter: ['hidden', 'style', 'class'] });
}

export function makeFt3dWindowDraggable(
    name: string,
    win: HTMLElement,
    bar: HTMLElement,
    frame: HTMLElement | null,
): void {
    if ((bar as unknown as { __ft3dDragBound?: boolean }).__ft3dDragBound) return;
    (bar as unknown as { __ft3dDragBound?: boolean }).__ft3dDragBound = true;
    bar.classList.add('ft3d-is-grabbable');

    bar.addEventListener('pointerdown', (event) => {
        // Never start a drag from the bar's own controls (tabs, close, minimise, or semantic
        // button-like content). The actor window clones its class/subclass and ancestry/lineage
        // info targets into the title bar as spans with role="button". Capturing their pointer on
        // the bar retargets the resulting click to the header, so the delegated openItemInfo action
        // never sees it and the Workspace appears to do nothing.
        const target = event.target instanceof Element ? event.target : null;
        if (event.button !== 0 || target?.closest('button, a, input, select, textarea, [role="button"]')) return;

        const frameRect = frameRectFor(win, frame);
        const winRect = win.getBoundingClientRect();
        const grabX = event.clientX - winRect.left;
        const grabY = event.clientY - winRect.top;
        win.classList.add('is-dragging');
        bar.setPointerCapture(event.pointerId);

        let last = { left: winRect.left - frameRect.left, top: winRect.top - frameRect.top };

        const onMove = (moveEvent: PointerEvent): void => {
            last = {
                left: moveEvent.clientX - frameRect.left - grabX,
                top: moveEvent.clientY - frameRect.top - grabY,
            };
            applyPosition(win, frame, last.left, last.top);
        };
        const onUp = (upEvent: PointerEvent): void => {
            bar.removeEventListener('pointermove', onMove);
            bar.removeEventListener('pointerup', onUp);
            bar.removeEventListener('pointercancel', onUp);
            win.classList.remove('is-dragging');
            try { bar.releasePointerCapture(upEvent.pointerId); } catch { /* already released */ }
            // Store the CLAMPED result, not the raw pointer delta, so a wild drag cannot persist an
            // off-frame position that then has to be discarded on the next load.
            const finalRect = win.getBoundingClientRect();
            const finalFrame = frameRectFor(win, frame);
            writePosition(name, {
                left: Math.round(finalRect.left - finalFrame.left),
                top: Math.round(finalRect.top - finalFrame.top),
            });
        };

        bar.addEventListener('pointermove', onMove);
        bar.addEventListener('pointerup', onUp);
        bar.addEventListener('pointercancel', onUp);
        event.preventDefault();
    });
}
